/** Canonical staff roles stored in users.role (JWT + localStorage). */
export const STAFF_ROLES = [
  'admin',
  'manager',
  'inventory_manager',
  'counter',
  'server',
  'kitchen',
  'accountant',
  // Kitchen flow (lane C). Only assignable with FEATURE_KITCHEN_FLOW=1 — the
  // staff form hides it and the backend refuses it without the flag.
  'kitchen_manager',
  // Delivery riders (F-DLV-N1). Only assignable with FEATURE_RIDER_APP=1 —
  // the staff form hides it and the backend refuses it without the flag.
  'rider',
] as const
export type StaffRole = (typeof STAFF_ROLES)[number]

export function isStaffRole(role: string): role is StaffRole {
  return (STAFF_ROLES as readonly string[]).includes(role)
}

const ROLE_ADMIN_PATHS: Record<StaffRole, string[] | null> = {
  admin: null,
  manager: [
    '/admin/counter',
    '/admin/server',
    '/admin/online-orders',
    // Table reservations + walk-in waitlist (F-RSV-N1; backend resvStaff).
    // The page shows a "switched off" note until an admin turns it on.
    '/admin/reservations',
    // Deliveries: assign riders, take their hand-ins (FEATURE_RIDER_APP;
    // backend group /dispatch). The page shows nothing without the flag.
    '/admin/dispatch',
    '/admin/delivery-zones',
    // The storefront editor (design, hours, featured items). Managers edited
    // it under Settings → Online Ordering before it had its own page.
    '/admin/website',
    // Bank card promos rotate every few months and the floor manager holds
    // the bank's campaign letter — deliberate manager surface (backend
    // group: adminManagerWrite). See audit/qa/01_auth_roles.csv.
    //
    // This is the ONLY /admin/marketing/* path a manager may open: the rest
    // of the Marketing Suite (promo codes, loyalty, campaigns, segments) is
    // admin-only. Listing the leaf path — not the '/admin/marketing' parent —
    // is what keeps that true, since canAccessAdminRoute matches by prefix.
    '/admin/marketing/card-promos',
    // Guest recovery worklist. The manager is in the building while the guest
    // still is, and waiting for the owner to log in is what turns a
    // recoverable 2-star into a public 1-star. Grants no new authority — a
    // manager can already mint a recovery coupon from the counter rail on a
    // manager PIN; this is the list of who needs one. The Reviews SETTINGS
    // page (Google link, gating, alert routing) stays admin-only, which is
    // why the worklist is a separate leaf path. Backend group:
    // adminMarketingRecovery.
    '/admin/marketing/recovery',
    // Reputation inbox — public Google reviews, same shift-work logic as the
    // recovery worklist above (backend group: adminMarketingRecovery). The
    // settings card inside the page renders admin-only, and the backend gates
    // reputation-settings / poll-now to admin regardless.
    '/admin/marketing/reputation',
    // Legacy top-level URL, kept so bookmarks survive; the route redirects to
    // the Marketing Suite tab above.
    '/admin/card-promos',
    '/admin/menu',
    '/admin/tables',
    '/admin/staff',
    '/admin/void-log',
    '/admin/settings',
    '/admin/stations',
    // Kitchen flow Stage 2B: catering bookings (backend cateringWrite).
    '/admin/catering',
    // Inventory (stock, purchasing, vendors read-only). Approvals — PO
    // approve, count approve, requisition issue, market buys — stay with
    // admin + inventory_manager. Backend groups: store, storeItemMaster,
    // storeAPRead, storeKitchenFlowPurchasing, kitchenFlowPrepWrite.
    '/admin/inventory',
    '/admin/day-close',
    '/admin/reports',
    '/admin/expenses',
    '/admin/customers',
    // F-FE-N192: the receipts inbox (FEATURE_RECEIPT_SCAN). Anyone who buys
    // or approves; the page itself hides what a role can't approve.
    '/admin/receipts',
    // Staff & Payroll (FEATURE_HR_PAYROLL, F-HR-N1): the daily register,
    // people and leave. No pay — the backend strips it and refuses money
    // routes (groups hrStaff + hrMark only).
    '/admin/hr',
    // Trade accounts (FEATURE_TRADE_ACCOUNTS, F-TRD-N1): clients, branches,
    // trade prices and deliveries (backend tradeRead + tradeEnter + tradeSetup).
    '/admin/trade',
  ],
  inventory_manager: ['/admin/inventory', '/admin/receipts'],
  // + Trade: records and confirms deliveries; never sets prices (backend
  // tradeRead + tradeEnter).
  counter: ['/admin/counter', '/admin/server', '/admin/online-orders', '/admin/reservations', '/admin/dispatch', '/admin/receipts', '/admin/trade'],
  // Floor tablet — ordering & Fire KOT only (no checkout / admin).
  server: ['/admin/server'],
  kitchen: ['/admin/kitchen', '/admin/stations'],
  // Read-only finance role: Reports & Analytics plus the expense ledger and
  // the P&L (backend groups: expenseLedgerRead + pnlRead, both GET-only for
  // this role). No entry, no edit, no catalogue changes.
  // + catering bookings, read-only (backend cateringRead).
  // + Staff & Payroll: reads the register, prepares payroll, records advances
  // and payouts (backend hrStaff + hrPay); approves only when the tenant
  // allows it, never their own run.
  // + Trade: reads clients, prices and deliveries (backend tradeRead).
  accountant: ['/admin/reports', '/admin/expenses', '/admin/catering', '/admin/receipts', '/admin/hr', '/admin/trade'],
  // Kitchen flow (lane C): raises requisitions and returns, receives what the
  // store issues, runs the KDS tablet. No inventory screen (costs are shown on
  // its own screens since Stage 2D — owner decision), no
  // purchasing (backend groups: /inventory-flow + /kitchen + adminStations).
  // + catering bookings: read, and send a confirmed booking to the kitchen.
  // + Staff & Payroll: marks the kitchen's register and leave, no pay.
  kitchen_manager: ['/admin/production', '/admin/kitchen', '/admin/stations', '/admin/catering', '/admin/receipts', '/admin/hr'],
  // Delivery rider on their own phone: their queue and nothing else (single
  // screen → immersive, like the server tablet). Backend group /rider.
  rider: ['/admin/rider'],
}

/**
 * First screen after login for each role. `posEnabled=false` (marketing
 * edition, see useEdition) reroutes everyone to a surface that edition
 * actually serves: admin lands on the Marketing Suite, everything else on the
 * Customer Directory. POS-only roles (counter/server/kitchen/inventory)
 * shouldn't exist on a marketing tenant; the directory is a safe read-only
 * fallback if one does.
 */
export function defaultAdminPath(role: string, posEnabled = true): string {
  if (!posEnabled) {
    return role === 'admin' ? '/admin/marketing/overview' : '/admin/customers'
  }
  switch (role) {
    case 'admin':
      return '/admin/dashboard'
    case 'manager':
      return '/admin/counter'
    case 'inventory_manager':
      return '/admin/inventory'
    case 'counter':
      return '/admin/counter'
    case 'server':
      return '/admin/server'
    case 'kitchen':
      return '/admin/kitchen'
    case 'accountant':
      return '/admin/reports'
    case 'kitchen_manager':
      return '/admin/production'
    case 'rider':
      return '/admin/rider'
    default:
      return '/admin/dashboard'
  }
}

/**
 * /admin/* path prefixes that only exist in the full POS edition. The route
 * guard in routes/admin.tsx bounces these when posEnabled is false; the
 * sidebar hides the matching sections via POS_ONLY_SECTION_IDS. Kept next to
 * ROLE_ADMIN_PATHS so both lists evolve together.
 */
export const POS_ONLY_ADMIN_PATH_PREFIXES = [
  '/admin/dashboard',
  '/admin/counter',
  '/admin/server',
  '/admin/online-orders',
  '/admin/reservations',
  '/admin/dispatch',
  '/admin/rider',
  '/admin/website',
  '/admin/delivery-zones',
  '/admin/day-close',
  '/admin/inventory',
  '/admin/production',
  '/admin/kitchen-flow',
  '/admin/catering',
  '/admin/menu',
  '/admin/tables',
  '/admin/kitchen',
  '/admin/stations',
  '/admin/void-log',
  '/admin/expenses',
  '/admin/receipts',
  '/admin/reports',
  '/admin/card-promos',
  '/admin/marketing/card-promos',
  '/admin/hr',
] as const

export function isPosOnlyAdminPath(pathname: string): boolean {
  return POS_ONLY_ADMIN_PATH_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  )
}

/** Whether this role may open the given /admin/* URL (exact prefix match). */
export function canAccessAdminRoute(role: string, pathname: string): boolean {
  if (!pathname.startsWith('/admin')) return false
  if (role === 'admin') return true
  const prefixes = ROLE_ADMIN_PATHS[role as StaffRole]
  if (!prefixes) return false
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

/**
 * True when this role has exactly ONE admin screen it may open (F-FE-N106).
 *
 * The immersive floor-tablet mode hides the sidebar, which is the only way to
 * change screens. That is right for a waiter — a `server` can reach nothing
 * else, so chrome would be decoration — and a trap for anyone who can. An admin
 * or manager who opened the server floor on the tablet had no way back to the
 * POS, or to anywhere, short of reinstalling the app.
 *
 * Derived from the route allowlist rather than testing `role === 'server'`, so
 * the day a server is given a second screen the navigation comes back on its
 * own instead of stranding them differently.
 */
export function hasSingleAdminScreen(role: string): boolean {
  const prefixes = ROLE_ADMIN_PATHS[role as StaffRole]
  // null = every route (admin). Unknown roles get chrome: being over-generous
  // with a back button is harmless, stranding someone is not.
  if (!prefixes) return false
  return prefixes.length === 1
}

/** Sidebar section ids visible for the role; null means all (admin). */
export function navSectionIdsForRole(role: string): Set<string> | null {
  if (role === 'admin') return null
  if (role === 'inventory_manager') return new Set(['inventory', 'receipts'])
  if (role === 'manager') {
    return new Set([
      'counter',
      'server',
      'online-orders',
      'reservations',
      'dispatch',
      'website',
      'delivery-zones',
      // Bank card promos moved inside the Marketing Suite; managers get the
      // section for that one tab. marketingLandingPath() sends them straight
      // to it, and MarketingShell hides the tabs they can't open.
      'marketing',
      'menu',
      'inventory',
      'tables',
      'staff',
      'void-log',
      'settings',
      'day-close',
      'reports',
      'expenses',
      'receipts',
      'customers',
      'catering',
      'hr',
    ])
  }
  if (role === 'counter') return new Set(['counter', 'server', 'online-orders', 'reservations', 'dispatch', 'receipts'])
  if (role === 'server') return new Set(['server'])
  if (role === 'kitchen') return new Set(['kitchen', 'stations'])
  if (role === 'accountant') return new Set(['reports', 'expenses', 'catering', 'receipts', 'hr'])
  if (role === 'kitchen_manager') return new Set(['production', 'kitchen', 'stations', 'catering', 'receipts', 'hr'])
  if (role === 'rider') return new Set(['rider'])
  return new Set()
}

/**
 * Where the "Marketing Suite" nav item points for this role. Managers may
 * only open the Bank card promos tab, so sending them to /overview — which
 * the route guard bounces — would make the nav item a dead link.
 */
export function marketingLandingPath(role: string): string {
  return role === 'manager' ? '/admin/marketing/card-promos' : '/admin/marketing/overview'
}

export function staffRoleLabel(role: string): string {
  switch (role) {
    case 'inventory_manager':
      return 'Inventory'
    case 'counter':
      return 'Counter'
    case 'server':
      return 'Server'
    case 'kitchen':
      return 'Kitchen'
    case 'admin':
      return 'Admin'
    case 'manager':
      return 'Manager'
    case 'accountant':
      return 'Accountant'
    case 'kitchen_manager':
      return 'Kitchen manager'
    case 'rider':
      return 'Rider'
    default:
      return role
  }
}

/**
 * The current user's role from localStorage (`pos_user`), or '' if unknown.
 * UI convenience only — real authorization is the backend route guard.
 */
export function getStoredUserRole(): string {
  try {
    const raw = localStorage.getItem('pos_user')
    if (!raw) return ''
    const parsed = JSON.parse(raw) as { role?: string }
    return parsed.role ?? ''
  } catch {
    return ''
  }
}

/**
 * The current user's id from localStorage (`pos_user`), or '' if unknown.
 * UI convenience only (e.g. hiding "change" on your own meal allowance); the
 * backend makes the real decision.
 */
export function getStoredUserId(): string {
  try {
    const raw = localStorage.getItem('pos_user')
    if (!raw) return ''
    const parsed = JSON.parse(raw) as { id?: string | number }
    return parsed.id != null ? String(parsed.id) : ''
  } catch {
    return ''
  }
}
